import GoogleAIStudioLoginDialog from "./engines/GoogleAIStudioLoginDialog";
import { useState } from "react";
import {
  Globe,
  MonitorSmartphone,
  RefreshCw,
  Trash2,
  Pencil,
  Mail,
  Clock,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { TID } from "../lib/testIds";

const METHOD_META = {
  playwright: { label: "Playwright", Icon: MonitorSmartphone },
  browser: { label: "Navegador manual", Icon: Globe },
};

const STATE_META = {
  connected: { label: "Conectado", dot: "bg-emerald-500", text: "text-emerald-400" },
  disconnected: { label: "Desconectado", dot: "bg-zinc-500", text: "text-zinc-400" },
  error: { label: "Error", dot: "bg-red-500", text: "text-red-400" },
  manual: { label: "Manual", dot: "bg-amber-500", text: "text-amber-400" },
  incomplete: { label: "Verificación incompleta", dot: "bg-amber-500", text: "text-amber-400" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function WebServiceCard({ service, onChange, onDeleted, onEdit }) {
  const [busy, setBusy] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const method = METHOD_META[service.access_method] || METHOD_META.playwright;
  // Effective state derives display from the persisted state so we never
  // misrepresent an unverified manual entry as truly "Conectado", and we
  // surface "Verificación incompleta" when a playwright service is linked
  // to a profile but has no logged_in_selector (falls back to health probe
  // only — the actual login is never checked).
  const effectiveKey = (() => {
    if (
      service.access_method === "browser" &&
      service.state === "connected"
    ) {
      return "manual";
    }
    if (
      service.access_method === "playwright" &&
      service.state === "connected" &&
      service.profile &&
      !service.logged_in_selector
    ) {
      return "incomplete";
    }
    return service.state;
  })();
  const state = STATE_META[effectiveKey] || STATE_META.disconnected;
  const MethodIcon = method.Icon;

  async function test() {
    setBusy("test");
    try {
      const { data } = await api.post(`/web-services/${service.id}/test`);
      // Backend returns { service, mode, ... } — service already has updated
      // state / last_checked_at / last_error persisted.
      const fresh = data?.service || service;
      onChange?.(fresh);
      let msg = "Conexión OK";
      if (data?.mode === "verify") {
        msg = data.logged_in
          ? `Sesión activa${data.page_title ? ` — ${data.page_title}` : ""}`
          : "Sesión no activa (necesita re-login)";
      } else if (data?.mode === "health") {
        msg = data.healthy
          ? `KRAI API operativa${typeof data.latency_ms === "number" ? ` (${data.latency_ms} ms)` : ""}`
          : data.message || "KRAI API no está sana";
      } else if (data?.mode === "manual") {
        msg = "Entrada de catálogo manual — sin verificación remota";
      }
      if (fresh.state === "error") {
        toast.error(fresh.last_error || msg);
      } else {
        toast.success(msg);
      }
    } catch (e) {
      // Backend embeds the updated service in detail.service on errors too
      const detail = e?.response?.data?.detail;
      const embedded = typeof detail === "object" && detail?.service;
      if (embedded) onChange?.(detail.service);
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function del() {
    if (!window.confirm(`¿Eliminar el servicio "${service.name}"?`)) return;
    setBusy("delete");
    try {
      await api.delete(`/web-services/${service.id}`);
      onDeleted?.(service.id);
      toast.success("Servicio eliminado");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      data-testid={TID.webServices.card(service.id)}
      className="rounded-md border border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden hover:border-zinc-700 transition-colors duration-150"
    >
      {["google-ai-studio", "qwen-web"].includes(service.profile) && <div className="px-5 pt-4">
        <Button size="sm" variant="outline" onClick={() => setLoginOpen(true)}>Iniciar sesión en {service.profile === "qwen-web" ? "Qwen" : "Google AI Studio"}</Button>
        {loginOpen && <GoogleAIStudioLoginDialog service={service} onClose={() => setLoginOpen(false)} onSaved={test} />}
      </div>}
      <div className="p-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md border border-zinc-800 bg-zinc-900 grid place-items-center">
              <Globe className="h-4 w-4 text-zinc-300" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{service.name}</div>
              <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider">
                Servicio web
              </div>
            </div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-2 text-[11px] font-medium ${state.text} shrink-0`}>
          <span className={`krai-dot ${state.dot}`} />
          {state.label}
        </div>
      </div>

      <div className="px-5 pb-4 space-y-3">
        <MetaRow label="URL">
          <a
            href={service.url}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-xs text-zinc-300 hover:text-white truncate block"
          >
            {service.url}
          </a>
        </MetaRow>
        <div className="grid grid-cols-2 gap-3">
          <MetaRow label="Usuario">
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300 truncate">
              <Mail className="h-3 w-3 text-zinc-500" />
              {service.user_or_email || <span className="text-zinc-600">sin configurar</span>}
            </span>
          </MetaRow>
          <MetaRow label="Método">
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
              <MethodIcon className="h-3 w-3 text-zinc-500" />
              {method.label}
            </span>
          </MetaRow>
        </div>
        <MetaRow label="Última prueba">
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
            <Clock className="h-3 w-3 text-zinc-500" />
            {formatDate(service.last_checked_at)}
          </span>
        </MetaRow>
        {service.access_method === "playwright" && service.profile && (
          <MetaRow label="Perfil vinculado">
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 font-mono">
              {service.profile}
              {service.logged_in_selector && (
                <span className="text-zinc-500 font-mono truncate" title={service.logged_in_selector}>
                  · {service.logged_in_selector}
                </span>
              )}
            </span>
          </MetaRow>
        )}
        {service.last_error && service.state === "error" && (
          <div className="rounded-md border border-red-900/40 bg-red-950/20 text-[11px] text-red-300 p-2 leading-relaxed">
            {service.last_error}
          </div>
        )}
        {service.notes && (
          <p className="text-xs text-zinc-500 line-clamp-3 pt-1">{service.notes}</p>
        )}
      </div>

      <div className="mt-auto border-t border-zinc-800 p-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          data-testid={TID.webServices.test(service.id)}
          disabled={busy !== null}
          onClick={test}
          className="bg-white text-black hover:bg-zinc-200 h-8"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Comprobar conexión
        </Button>
        <Button
          size="sm"
          variant="secondary"
          data-testid={TID.webServices.edit(service.id)}
          disabled={busy !== null}
          onClick={() => onEdit?.(service)}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200"
        >
          <Pencil className="h-3.5 w-3.5 mr-1.5" />
          Editar
        </Button>
        <button
          data-testid={TID.webServices.delete(service.id)}
          onClick={del}
          disabled={busy !== null}
          className="ml-auto h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition-colors duration-150"
          title="Eliminar"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function MetaRow({ label, children }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        {label}
      </div>
      {children}
    </div>
  );
}
